// Combat odds — the probability an attacker conquers a territory in an all-out
// attack, given army counts AND the die sizes each side rolls across the border.
// Pure + deterministic (no RNG): used by the bot AI to make a CALCULATED attack
// decision instead of the old crude "only attack when I have more armies than
// the defender" rule (which left bots passive at parity, even though 3 attacking
// dice beat 2 defending dice).
//
// Model: the single-roll outcome distribution (ties to the defender), iterated
// as a Markov chain until one side can no longer continue. The attacker rolls
// min(3, A-1) dice, the defender min(2, D); the attacker conquers when D hits 0,
// and fails when it's reduced to 1 army (0 attack dice).
//
// Die SIZES default to six on both sides, which reproduces the canonical Risk
// tables exactly (see tests/engine/combatOdds.test.ts, which asserts the numeric
// model against the hand-written 36 / 216 / 1296 / 7776 fractions). Maps may
// override them per border and per side via `Territory.borderDice` — Capture the
// Flag's towers attack d9, its forts defend d7 and its flags defend d5 — so the
// bot MUST evaluate odds with the real dice or it grossly mis-prices those
// borders (a d5 flag is far softer than its army count suggests; a d8 gate far
// harder).

import {
  DEFAULT_DICE_SIDES,
  rollOutcomes,
  type RollOutcome,
} from '@warcarp/api-types'

// Compatibility export for existing engine and test imports. The exact
// per-roll distribution now lives in the shared package so mobile luck stats
// and bot conquest odds use identical math.
export { rollOutcomes }

// Keyed by (attacker, defender, sides, sides), so the key space grows with the
// LARGEST stack ever seen rather than with any rule constant. Left unbounded it
// reaches V8's ~16.7M Map ceiling and throws `Map maximum size exceeded`
// mid-run — reachable in ordinary lab campaigns once a policy calls this per
// border per roll-forward step, and a slow leak in the long-lived API process.
//
// Bounding it is only safe because the computation below is ITERATIVE. The
// recursive form reduced `attacker + defender` by at most `min(attackerDice,
// defenderDice)` per call, so a cold call recursed O(attacker + defender) deep;
// it never overflowed only because the unbounded cache was always warm from
// smaller positions. Evicting that cache without removing the recursion trades
// a memory crash for a stack crash. The bottom-up fill removes both.
// One million entries is ~100x ordinary play and roughly 100MB of Map; the
// first cut at 4M let a five-seat placement-search run push the machine into
// swap, which is the failure the bound exists to prevent.
const CONQUEST_MEMO_LIMIT = 1_000_000
const conquestMemo = new Map<string, number>()

/**
 * Largest stack pair stored in the full dynamic-programming table. Larger
 * pairs use the exact rolling-row calculation and retain only their final
 * probability in a separate bounded cache. The threshold controls storage,
 * not gameplay legality or numerical accuracy.
 */
export const MAX_TRACKED_ARMIES = 300

/** Test-only cache observation; gameplay never reads these. */
export function conquestMemoSize(): number {
  return conquestMemo.size
}
export function clearConquestMemo(): void {
  conquestMemo.clear()
  largePairCache.clear()
}

/**
 * The six per-roll outcome tables a fill can need (1–3 attacker dice × 1–2
 * defender dice) for one dice pair, resolved once instead of via a string-keyed
 * lookup per cell. This reduces the fill's constant cost without changing its
 * arithmetic or asymptotic complexity.
 */
function outcomeTables(attackerSides: number, defenderSides: number): ReadonlyArray<RollOutcome>[][] {
  const tables: ReadonlyArray<RollOutcome>[][] = []
  for (let aDice = 1; aDice <= 3; aDice++) {
    tables[aDice] = []
    for (let dDice = 1; dDice <= 2; dDice++) {
      tables[aDice]![dDice] = rollOutcomes(aDice, dDice, attackerSides, defenderSides)
    }
  }
  return tables
}

/**
 * Small insertion-ordered LRU for pairs above the tracked domain, so a live
 * game reuses a large pair's exact result while it remains cached. Eviction or
 * a new pair requires another synchronous fill; this bounds retained memory
 * and repeated work, but does not bound cold-call latency.
 */
const LARGE_PAIR_CACHE_LIMIT = 512
const largePairCache = new Map<string, number>()

/**
 * Exact odds above the full-table domain, using O(defender) working memory.
 * Each cell depends on rows a, a-1, a-2 and columns d, d-1, d-2. The three-row
 * fill preserves the full table's summation order and caches its final result.
 * Cold computation remains O(attacker × defender); large legal stacks can
 * still occupy the API event loop for hundreds of milliseconds.
 */
function exactUncached(attackerArmies: number, defenderArmies: number,
  attackerSides: number, defenderSides: number): number {
  const cacheKey = `${attackerArmies},${defenderArmies},${attackerSides},${defenderSides}`
  const cached = largePairCache.get(cacheKey)
  if (cached !== undefined) {
    // Refresh recency.
    largePairCache.delete(cacheKey)
    largePairCache.set(cacheKey, cached)
    return cached
  }
  const tables = outcomeTables(attackerSides, defenderSides)
  const width = defenderArmies + 1
  let rowMinus2 = new Float64Array(width)
  let rowMinus1 = new Float64Array(width)
  let row = new Float64Array(width)
  // Row a = 1 (and any a <= 1): attacker cannot roll, so 0 everywhere except d = 0.
  rowMinus1[0] = 1
  rowMinus2[0] = 1
  for (let a = 2; a <= attackerArmies; a++) {
    row = new Float64Array(width)
    row[0] = 1
    const aDice = Math.min(3, a - 1)
    const byDefenderDice = tables[aDice]!
    for (let d = 1; d <= defenderArmies; d++) {
      let p = 0
      for (const [prob, attLost, defLost] of byDefenderDice[Math.min(2, d)]!) {
        const nextDefender = d - defLost
        const nextAttacker = a - attLost
        const sub = nextDefender <= 0 ? 1
          : nextAttacker <= 1 ? 0
          : attLost === 0 ? (row[nextDefender] as number)
          : attLost === 1 ? (rowMinus1[nextDefender] as number)
          : (rowMinus2[nextDefender] as number)
        p += prob * sub
      }
      row[d] = Math.max(0, Math.min(1, p))
    }
    rowMinus2 = rowMinus1
    rowMinus1 = row
  }
  const result = row[defenderArmies] as number
  if (largePairCache.size >= LARGE_PAIR_CACHE_LIMIT) {
    largePairCache.delete(largePairCache.keys().next().value as string)
  }
  largePairCache.set(cacheKey, result)
  return result
}

/**
 * Fill every `(a, d)` cell up to the requested one, ascending.
 *
 * Each cell depends on `(a - attackerLost, d - defenderLost)` where the two
 * losses sum to at least one: `attackerLost === 0` means the same row and a
 * strictly smaller `d` (already filled this row), otherwise a strictly smaller
 * `a` (an earlier row, any `d`). So a row-ascending sweep always reads settled
 * values, in the same `rollOutcomes` order and with the same operands as the
 * recursion — the results are identical floats, not merely close.
 */
function fillConquestTable(attackerArmies: number, defenderArmies: number,
  attackerSides: number, defenderSides: number): void {
  const tables = outcomeTables(attackerSides, defenderSides)
  for (let a = 2; a <= attackerArmies; a++) {
    const aDice = Math.min(3, a - 1)
    const byDefenderDice = tables[aDice]!
    for (let d = 1; d <= defenderArmies; d++) {
      const key = `${a},${d},${attackerSides},${defenderSides}`
      if (conquestMemo.has(key)) continue
      let p = 0
      for (const [prob, attLost, defLost] of byDefenderDice[Math.min(2, d)]!) {
        const nextAttacker = a - attLost
        const nextDefender = d - defLost
        const sub = nextDefender <= 0 ? 1
          : nextAttacker <= 1 ? 0
          : conquestMemo.get(`${nextAttacker},${nextDefender},${attackerSides},${defenderSides}`) as number
        p += prob * sub
      }
      // Guard tiny FP drift outside [0, 1].
      conquestMemo.set(key, Math.max(0, Math.min(1, p)))
    }
  }
}

/**
 * Probability that an attacker with `attackerArmies` total armies conquers a
 * defender with `defenderArmies`, attacking all-out (keep rolling until the
 * defender is wiped out or the attacker can no longer roll).
 *
 * `attackerSides` / `defenderSides` are the die sizes each side rolls ACROSS
 * THIS BORDER — read them from the map with `attackDiceSides(from, to.id)` and
 * `defendDiceSides(to, from.id)`. Both default to six, so every caller and map
 * that predates per-border dice is unaffected.
 *
 * Returns a value in [0, 1]. Conquest requires the attacker to keep ≥1 army to
 * occupy, so an attacker reduced to 1 army has failed.
 *
 * @example
 *   conquestProbability(10, 1)        // ≈ 0.99 — overwhelming
 *   conquestProbability(4, 4)         // ≈ 0.46 — slightly unfavourable at parity
 *   conquestProbability(1, 3)         // 0      — can't attack with 1 army
 *   conquestProbability(4, 4, 6, 5)   // ≈ 0.79 — same armies, defender rolls d5
 *   conquestProbability(4, 4, 6, 8)   // ≈ 0.13 — same armies, defender rolls d8
 */
export function conquestProbability(
  attackerArmies: number,
  defenderArmies: number,
  attackerSides: number = DEFAULT_DICE_SIDES,
  defenderSides: number = DEFAULT_DICE_SIDES,
): number {
  if (defenderArmies <= 0) return 1
  if (attackerArmies <= 1) return 0
  if (attackerArmies > MAX_TRACKED_ARMIES || defenderArmies > MAX_TRACKED_ARMIES) {
    return exactUncached(attackerArmies, defenderArmies, attackerSides, defenderSides)
  }

  const key = `${attackerArmies},${defenderArmies},${attackerSides},${defenderSides}`
  const cached = conquestMemo.get(key)
  if (cached !== undefined) return cached

  // Evict only at the top of a call, never mid-fill: every value is a pure
  // function of its key, so a dropped entry is recomputed identically.
  if (conquestMemo.size >= CONQUEST_MEMO_LIMIT) conquestMemo.clear()
  fillConquestTable(attackerArmies, defenderArmies, attackerSides, defenderSides)
  return conquestMemo.get(key) as number
}

/** Probability one `attackerSides`-sided die beats one `defenderSides`-sided die (ties lose). */
export function singleRollWinProbability(
  attackerSides: number = DEFAULT_DICE_SIDES,
  defenderSides: number = DEFAULT_DICE_SIDES,
): number {
  let wins = 0
  for (let a = 1; a <= attackerSides; a++) {
    wins += Math.min(a - 1, defenderSides)
  }
  return wins / (attackerSides * defenderSides)
}

/** The classic d6-vs-d6 per-pair win probability (15/36) — the neutral baseline. */
const BASELINE_PAIR_WIN = singleRollWinProbability(DEFAULT_DICE_SIDES, DEFAULT_DICE_SIDES)

/**
 * How much harder one side hits across a border than the classic d6-vs-d6 pair,
 * as a multiplier centred on **exactly 1.0** for d6-vs-d6.
 *
 * This is the map-data-driven "how scary is this border" primitive the bots use
 * to weight an army count by the dice behind it: a d9 tower facing a d5 flag
 * pressures ~1.6× as hard as a plain d6 border, while a d6 attacker facing a d8
 * gate manages only ~0.75×. Because it is exactly 1.0 at the defaults, every map
 * without `borderDice` scores identically to before.
 */
export function borderPressure(
  attackerSides: number = DEFAULT_DICE_SIDES,
  defenderSides: number = DEFAULT_DICE_SIDES,
): number {
  return singleRollWinProbability(attackerSides, defenderSides) / BASELINE_PAIR_WIN
}

/**
 * Minimum conquest probability at which a bot will launch an attack, derived
 * from its (effective) aggression weight. More aggressive personalities accept
 * worse odds; the result is clamped so even the most aggressive bot won't make
 * clearly suicidal attacks, and the most cautious still attacks when favoured.
 *
 * Centred at ~0.35 for the default aggression (0.8). Calibration reference
 * (conquestProbability): a small +1 advantage like 3v2 is only ~0.36 and
 * big-stack parity like 5v5 is ~0.36 — so a higher threshold would make bots
 * SKIP the very attacks players expect them to make. 0.35 keeps them taking
 * slight advantages (like the old "must outnumber" rule did) AND big-stack
 * parity (which it did NOT — that's the new aggression). Aggressive
 * personalities drop toward ~0.28: they'll trade into near-parity / slight
 * disadvantages to keep the pressure on. The floor stops anyone from making
 * clearly-losing attacks. This is the fix for "bots just let me take
 * territories and don't attack back".
 */
export function attackOddsThreshold(aggression: number): number {
  const DEFAULT_AGGRESSION = 0.8
  const threshold = 0.35 - (aggression - DEFAULT_AGGRESSION) * 0.2
  return Math.max(0.28, Math.min(0.5, threshold))
}
